import * as React from "react";
import { MoreHorizontal } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "./sheet.js";
import { cn } from "../lib/cn.js";

/**
 * Mobile bottom navigation
 * ------------------------
 * Renders a fixed bar at the bottom of the viewport visible only at
 * widths below the `md` breakpoint (768px). Above `md` the desktop
 * sidebar takes over and this primitive renders nothing.
 *
 * Layout: 5 equal slots — 4 primary nav items + 1 "More" trigger that
 * opens a bottom Sheet listing the overflow items.
 *
 * Touch targets are ≥44×44px (Apple HIG) achieved by `min-h-[56px]`
 * cells. The bar reserves bottom safe-area inset for notched iOS so
 * the items don't sit under the home indicator.
 *
 * Animation policy (Emil framework, Iron Rule #6): the bar itself is
 * NOT animated on mount — it's a permanent fixture, animating it would
 * be visual noise. The "More" sheet animates in from the bottom edge
 * (Radix Sheet's built-in slide-in-from-bottom). Active-item indicator
 * is instantaneous — route navigation happens many times per session
 * and animations there feel sluggish.
 */

interface MobileBottomNavProps extends React.HTMLAttributes<HTMLElement> {}

export const MobileBottomNav = React.forwardRef<HTMLElement, MobileBottomNavProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <nav
        ref={ref}
        aria-label="Primary mobile"
        className={cn(
          // Visibility: only on mobile + small tablets.
          "fixed inset-x-0 bottom-0 z-40 flex md:hidden",
          // Surface — translucent so content behind ghost-shows on scroll;
          // backdrop-blur keeps text legible.
          "border-border bg-card/85 supports-[backdrop-filter]:bg-card/70 border-t backdrop-blur-md",
          // Safe-area inset for iOS home indicator. `pb-[env(...)]`
          // gracefully degrades to 0 on Android / desktop browsers.
          "pb-[env(safe-area-inset-bottom)]",
          className,
        )}
        {...props}
      >
        <ul className="grid w-full grid-cols-5">{children}</ul>
      </nav>
    );
  },
);
MobileBottomNav.displayName = "MobileBottomNav";

export interface MobileBottomNavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

/**
 * A single primary slot. Renders an anchor sized for thumb taps with
 * an icon + small label. Active state uses a tinted pill background
 * + foreground text (not a coloured underline — those are too subtle
 * at this scale).
 */
export const MobileBottomNavItem = React.forwardRef<HTMLAnchorElement, MobileBottomNavItemProps>(
  ({ icon, label, active, className, ...props }, ref) => {
    return (
      <li className="contents">
        <a
          ref={ref}
          data-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          className={cn(
            // Layout
            "group/mbnitem relative flex min-h-[56px] flex-col items-center justify-center gap-0.5 px-1 py-1.5",
            // Default colours — muted icon + smaller label
            "text-muted-foreground",
            // Touch behaviour: instant feedback on press
            "transition-colors duration-100",
            "active:bg-muted/60",
            // Focus ring lives inside the cell so it doesn't bleed into
            // neighbouring slots
            "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
            // Active state: pill underneath the icon + foreground colour
            "data-[active=true]:text-foreground",
            className,
          )}
          {...props}
        >
          <span
            aria-hidden="true"
            className={cn(
              // The active-state pill — only rendered visually when active.
              "grid h-7 w-12 place-items-center rounded-full transition-colors",
              "[&_svg]:size-5",
              "group-data-[active=true]/mbnitem:bg-accent group-data-[active=true]/mbnitem:text-accent-foreground",
            )}
          >
            {icon}
          </span>
          <span className="text-[10px] font-medium leading-none tracking-wide">{label}</span>
        </a>
      </li>
    );
  },
);
MobileBottomNavItem.displayName = "MobileBottomNavItem";

export interface MobileBottomNavMoreProps {
  children: React.ReactNode;
  /** Label under the "More" icon. Defaults to "More". */
  label?: string;
  /** Optional brand block at the top of the sheet (mirrors sidebar header). */
  header?: React.ReactNode;
  /** Optional footer (e.g. user card + sign-out). */
  footer?: React.ReactNode;
  /** Sheet title for assistive tech. Defaults to "Navigation". */
  sheetTitle?: string;
  /** Sheet description for assistive tech. */
  sheetDescription?: string;
}

/**
 * The 5th slot — opens a bottom Sheet that scrolls if the overflow
 * list is tall. Overflow items use the same NavLink wrapping pattern
 * the desktop sidebar uses: caller wraps each in NavLink and passes
 * a `MobileBottomNavSheetItem` as the child.
 */
export function MobileBottomNavMore({
  children,
  label = "More",
  header,
  footer,
  sheetTitle = "Navigation",
  sheetDescription,
}: MobileBottomNavMoreProps) {
  return (
    <li className="contents">
      <Sheet>
        <SheetTrigger asChild>
          <button
            type="button"
            className={cn(
              "group/mbnmore relative flex min-h-[56px] flex-col items-center justify-center gap-0.5 px-1 py-1.5",
              "text-muted-foreground transition-colors duration-100",
              "active:bg-muted/60",
              "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset",
              "data-[state=open]:text-foreground",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "grid h-7 w-12 place-items-center rounded-full transition-colors",
                "[&_svg]:size-5",
                "group-data-[state=open]/mbnmore:bg-accent group-data-[state=open]/mbnmore:text-accent-foreground",
              )}
            >
              <MoreHorizontal />
            </span>
            <span className="text-[10px] font-medium leading-none tracking-wide">{label}</span>
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            // The bottom sheet wears the surface colour, not the page bg,
            // for a subtle elevation contrast against the content underneath.
            "rounded-t-2xl border-t-0 p-0",
            // Cap height so even on a tablet-sized phone the sheet leaves
            // room for the page header behind it; long lists scroll inside.
            "max-h-[85svh]",
          )}
        >
          {/* Drag affordance — visual hint that the sheet can be dismissed
              by swipe (Radix doesn't actually wire swipe-to-dismiss, but the
              affordance still matches user expectation from native iOS/Android). */}
          <div className="flex justify-center pb-1 pt-2.5">
            <span aria-hidden="true" className="bg-muted-foreground/30 h-1 w-10 rounded-full" />
          </div>

          <SheetHeader className="px-5 pb-3 pt-1">
            <SheetTitle>{sheetTitle}</SheetTitle>
            {sheetDescription ? <SheetDescription>{sheetDescription}</SheetDescription> : null}
            {header ? <div className="pt-2">{header}</div> : null}
          </SheetHeader>

          <div className="max-h-[60svh] overflow-y-auto px-3 pb-3">
            <ul className="grid gap-0.5">{children}</ul>
          </div>

          {footer ? <div className="border-border border-t px-5 py-3">{footer}</div> : null}
        </SheetContent>
      </Sheet>
    </li>
  );
}

export interface MobileBottomNavSheetItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  /** Optional trailing badge (count, "Beta", etc.). */
  trailing?: React.ReactNode;
}

/**
 * Row inside the "More" sheet. Wider than the bottom-nav cells — full
 * width with icon left, label, optional trailing badge. Tapping
 * navigates AND closes the sheet (handled via SheetClose wrapping in
 * the caller, or the Radix `data-state` flip closes it once route
 * changes settle).
 */
export const MobileBottomNavSheetItem = React.forwardRef<
  HTMLAnchorElement,
  MobileBottomNavSheetItemProps
>(({ icon, label, active, trailing, className, ...props }, ref) => {
  // The anchor is wrapped in <SheetClose asChild> by the consumer so
  // a tap closes the sheet. We can't do that here without coupling to
  // Radix's internal state, so it stays the consumer's job.
  return (
    <li>
      <a
        ref={ref}
        data-active={active ? "true" : undefined}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
          "text-foreground hover:bg-muted",
          "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
          "data-[active=true]:bg-accent data-[active=true]:text-accent-foreground data-[active=true]:font-medium",
          className,
        )}
        {...props}
      >
        <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center [&_svg]:size-5">
          {icon}
        </span>
        <span className="flex-1 truncate">{label}</span>
        {trailing ? <span className="shrink-0">{trailing}</span> : null}
      </a>
    </li>
  );
});
MobileBottomNavSheetItem.displayName = "MobileBottomNavSheetItem";

// Re-export SheetClose so consumers can wrap MobileBottomNavSheetItem with it
// without reaching into the sheet primitive directly.
export { SheetClose as MobileBottomNavSheetClose };
